// ./src/components/features/LostFound/lostFoundLabels.ts
//
// ROADMAP #68 — the words and colours lost & found shows for its server values (categories, states, how ownership was
// checked, what happened to an unclaimed item, who may use the public forms). ONE place, so the lists, drawers and the
// settings say the same thing. English defaults through `t()`, like every other feature here.
import type { TFunction } from "i18next";

import { LOST_FOUND_AUDIENCES, LOST_FOUND_CATEGORIES, LOST_FOUND_DISPOSAL_KINDS, LOST_FOUND_VERIFICATION_KINDS } from "@/utils/api";
import { utcToZonedString } from "@/utils";

/**
 * Now on the event's clock, to the minute ("YYYY-MM-DD HH:mm:00") — what a found item's time starts as, and the latest a
 * found / lost time may be (owner, 2026-10-23: never in the future; the server refuses it too). Read when used, so a
 * drawer left open keeps up.
 */
export const lostFoundNowOnClock = (tz: string | null): string | null => {
  const now = utcToZonedString(new Date().toISOString(), tz, false);

  return now ? `${now.slice(0, 16)}:00` : null;
};

const CATEGORY: Record<string, { label: string; icon: string }> = {
  phone: { label: "Phone", icon: "DeviceMobile" },
  wallet: { label: "Wallet or purse", icon: "Wallet" },
  keys: { label: "Keys", icon: "Key" },
  bag: { label: "Bag", icon: "Backpack" },
  clothing: { label: "Clothing", icon: "Shirt" },
  glasses: { label: "Glasses", icon: "Eyeglass" },
  jewellery: { label: "Jewellery or watch", icon: "Diamond" },
  id_card: { label: "ID or card", icon: "Id" },
  bottle: { label: "Bottle or cup", icon: "Bottle" },
  child_item: { label: "Child's item", icon: "BabyCarriage" },
  electronics: { label: "Electronics", icon: "Headphones" },
  other: { label: "Something else", icon: "Package" },
};

export const lostFoundCategoryLabel = (t: TFunction, category: string | null | undefined): string =>
  t(`lostFound:categories.${category}`, { defaultValue: CATEGORY[String(category)]?.label ?? String(category ?? "") });

export const lostFoundCategoryIcon = (category: string | null | undefined): string => CATEGORY[String(category)]?.icon ?? "Package";

export const lostFoundCategoryOptions = (t: TFunction): { value: string; label: string }[] =>
  LOST_FOUND_CATEGORIES.map((c) => ({ value: c, label: lostFoundCategoryLabel(t, c) }));

const ITEM_STATE: Record<string, { label: string; color: string }> = {
  reported: { label: "On its way", color: "yellow" },
  held: { label: "At the desk", color: "blue" },
  returned: { label: "Returned", color: "green" },
  disposed: { label: "Disposed of", color: "gray" },
  not_received: { label: "Never arrived", color: "gray" },
};

export const lostFoundItemStateLabel = (t: TFunction, state: string | null | undefined): string =>
  t(`lostFound:itemStates.${state}`, { defaultValue: ITEM_STATE[String(state)]?.label ?? String(state ?? "") });

export const lostFoundItemStateColor = (state: string | null | undefined): string => ITEM_STATE[String(state)]?.color ?? "gray";

export const lostFoundItemStateOptions = (t: TFunction): { value: string; label: string }[] =>
  Object.keys(ITEM_STATE).map((s) => ({ value: s, label: lostFoundItemStateLabel(t, s) }));

const REPORT_STATE: Record<string, { label: string; color: string }> = {
  open: { label: "Looking", color: "yellow" },
  matched: { label: "Maybe found", color: "blue" },
  returned: { label: "Returned", color: "green" },
  closed: { label: "Closed", color: "gray" },
};

export const lostReportStateLabel = (t: TFunction, state: string | null | undefined): string =>
  t(`lostFound:reportStates.${state}`, { defaultValue: REPORT_STATE[String(state)]?.label ?? String(state ?? "") });

export const lostReportStateColor = (state: string | null | undefined): string => REPORT_STATE[String(state)]?.color ?? "gray";

export const lostReportStateOptions = (t: TFunction): { value: string; label: string }[] =>
  Object.keys(REPORT_STATE).map((s) => ({ value: s, label: lostReportStateLabel(t, s) }));

const CLOSED_REASON: Record<string, string> = {
  not_found: "Not found",
  withdrawn: "Withdrawn",
  expired: "Kept long enough",
};

export const lostReportClosedReasonLabel = (t: TFunction, reason: string | null | undefined): string =>
  t(`lostFound:closedReasons.${reason}`, { defaultValue: CLOSED_REASON[String(reason)] ?? String(reason ?? "") });

const VERIFICATION: Record<string, string> = {
  described_detail: "They described something not on the list",
  id_checked: "ID checked",
  qr_tag: "Their QR tag",
  known_guest: "A guest we know",
  other: "Another way",
};

export const lostFoundVerificationLabel = (t: TFunction, kind: string | null | undefined): string =>
  t(`lostFound:verification.${kind}`, { defaultValue: VERIFICATION[String(kind)] ?? String(kind ?? "") });

export const lostFoundVerificationOptions = (t: TFunction): { value: string; label: string }[] =>
  LOST_FOUND_VERIFICATION_KINDS.map((k) => ({ value: k, label: lostFoundVerificationLabel(t, k) }));

const DISPOSAL: Record<string, string> = {
  discarded: "Thrown away",
  donated: "Donated",
  to_police: "Handed to the police",
  to_venue: "Handed to the venue",
};

export const lostFoundDisposalLabel = (t: TFunction, kind: string | null | undefined): string =>
  t(`lostFound:disposal.${kind}`, { defaultValue: DISPOSAL[String(kind)] ?? String(kind ?? "") });

export const lostFoundDisposalOptions = (t: TFunction): { value: string; label: string }[] =>
  LOST_FOUND_DISPOSAL_KINDS.map((k) => ({ value: k, label: lostFoundDisposalLabel(t, k) }));

const AUDIENCE: Record<string, string> = {
  off: "Nobody",
  guests: "Guests of this event",
  everyone: "Everyone",
};

export const lostFoundAudienceOptions = (t: TFunction): { value: string; label: string }[] =>
  LOST_FOUND_AUDIENCES.map((a) => ({ value: a, label: t(`lostFound:audiences.${a}`, { defaultValue: AUDIENCE[a] }) }));

/** "Item 37" / "Report 12". */
export const lostFoundNumber = (prefix: "Item" | "Report", n: number | null | undefined): string => (n != null ? `${prefix} ${n}` : prefix);
